import { useState, useEffect, useCallback } from "react";
import type { FeedbackResponse } from "@snaptics/api/src/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { getScreenshotUrl } from "~/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface FeedbackDetailProps {
  feedback: FeedbackResponse;
}

export function FeedbackDetail({ feedback }: FeedbackDetailProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const typeBadgeVariant = feedback.type === "bug" ? "destructive" : "default";
  const statusBadgeVariant = feedback.status === "done" ? "secondary" : "outline";

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const screenshotUrl = getScreenshotUrl(feedback.screenshot_url || undefined);
  const videoUrl = getScreenshotUrl(feedback.video_url || undefined);

  // Unified image list: screenshot first, then attachments
  const allImages: string[] = [
    ...(screenshotUrl ? [screenshotUrl] : []),
    ...(feedback.attachment_urls ?? []),
  ];

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const prev = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  const next = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i + 1) % allImages.length);
  }, [allImages.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, prev, next]);

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant={typeBadgeVariant}>
              {feedback.type === "bug" ? "Bug" : "Idea"}
            </Badge>
            <Badge variant={statusBadgeVariant}>
              {feedback.status.replace("_", " ")}
            </Badge>
          </div>
          <CardTitle className="text-2xl">{feedback.title}</CardTitle>
          <CardDescription>
            Created {formatDate(feedback.created_at)}
            {feedback.updated_at !== feedback.created_at && (
              <> • Updated {formatDate(feedback.updated_at)}</>
            )}
          </CardDescription>
        </CardHeader>
        {feedback.description && (
          <CardContent>
            <p className="text-base whitespace-pre-wrap">{feedback.description}</p>
          </CardContent>
        )}
      </Card>

      {/* Images Card — screenshot + attachments unified */}
      {allImages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              Images{" "}
              <span className="text-muted-foreground font-normal text-sm">
                ({allImages.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {allImages.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Image ${i + 1}`}
                  className="rounded-lg border aspect-square object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => openLightbox(i)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Card */}
      {videoUrl && (
        <Card>
          <CardHeader>
            <CardTitle>Video</CardTitle>
          </CardHeader>
          <CardContent>
            <video
              src={videoUrl}
              controls
              className="rounded-lg border w-full"
            />
          </CardContent>
        </Card>
      )}

      {/* Submitter Information Card */}
      {(feedback.submitter_name || feedback.submitter_email) && (
        <Card>
          <CardHeader>
            <CardTitle>Submitter Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {feedback.submitter_name && (
              <div>
                <p className="text-sm font-medium">Name</p>
                <p className="text-sm text-muted-foreground">
                  {feedback.submitter_name}
                </p>
              </div>
            )}
            {feedback.submitter_email && (
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-sm text-muted-foreground">
                  {feedback.submitter_email}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Technical Details Card */}
      {feedback.metadata && Object.keys(feedback.metadata).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Technical Details</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
              {JSON.stringify(feedback.metadata, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Image Lightbox Carousel */}
      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && closeLightbox()}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Image viewer</DialogTitle>
            <DialogDescription>
              Use arrow keys or buttons to navigate. Press Esc to close.
            </DialogDescription>
          </DialogHeader>

          {lightboxIndex !== null && (
            <div className="relative flex items-center justify-center min-h-[60vh]">
              <img
                key={lightboxIndex}
                src={allImages[lightboxIndex]}
                alt={`Image ${lightboxIndex + 1} of ${allImages.length}`}
                className="max-h-[80vh] max-w-full object-contain"
              />

              {/* Prev button */}
              {allImages.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 text-white hover:bg-white/20"
                  onClick={prev}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
              )}

              {/* Next button */}
              {allImages.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 text-white hover:bg-white/20"
                  onClick={next}
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              )}

              {/* Counter */}
              {allImages.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                  {lightboxIndex + 1} / {allImages.length}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
